"use client";

import {useCallback, useState} from "react";
import type {MonthRating, ContentSection as ContentSectionType, Festival} from "@/lib/content-parser";
import {PreferenceFilterBar} from "./PreferenceFilterBar";
import {PreferenceMonthGrid} from "./PreferenceMonthGrid";
import {ContentSection} from "./ContentSection";
import {getPreferenceColour} from "./preference-colours";

interface Preference {
    id: string;
    label: string;
}

interface PreferenceSectionProps {
    months: MonthRating[];
    preferences: Preference[];
    selectedCountLabel: string;
    selectLabel: string;
    sections?: ContentSectionType[];
    sectionsHeading?: string;
    festivals?: Festival[];
    festivalHeading?: string;
}

export function PreferenceSection({months, preferences, selectedCountLabel, selectLabel, sections, sectionsHeading, festivals, festivalHeading}: PreferenceSectionProps) {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const toggle = useCallback((id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    // Build a lookup from section id to preference index for colour mapping
    const sectionIndexMap = new Map(preferences.map((p, i) => [p.id, i]));

    return (
        <>
            {preferences.length > 0 && (
                <PreferenceFilterBar
                    preferences={preferences}
                    selectedIds={selectedIds}
                    onToggle={toggle}
                    selectedCountLabel={selectedCountLabel.replace("{count}", String(selectedIds.size))}
                    selectLabel={selectLabel}
                />
            )}
            <PreferenceMonthGrid
                months={months}
                preferences={preferences}
                selectedIds={selectedIds}
                festivals={festivals}
                festivalHeading={festivalHeading}
            />
            {sections && sections.length > 0 && (
                <section className="mt-10 space-y-8">
                    {sectionsHeading && (
                        <h2 className="text-2xl font-semibold text-foreground">
                            {sectionsHeading}
                        </h2>
                    )}
                    {sections.map((section) => {
                        const index = sectionIndexMap.get(section.id) ?? 0;
                        return (
                            <ContentSection
                                key={section.id}
                                section={section}
                                colour={getPreferenceColour(index)}
                                isSelected={selectedIds.has(section.id)}
                            />
                        );
                    })}
                </section>
            )}
        </>
    );
}
